import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, BookOpen, Trash2, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { bookContent } from '../data/bookContent';
import { toast } from 'sonner';
import { CloudSync } from '../components/CloudSync';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export const MyJournal = () => {
  const [entries, setEntries] = useState([]);
  const [expandedChapters, setExpandedChapters] = useState({});

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = () => {
    const allEntries = [];
    bookContent.chapters.forEach((chapter) => {
      const chapterEntries = [];
      chapter.journalQuestions.forEach((question, qIndex) => {
        const storageKey = `journal-ch${chapter.id}-q${qIndex}`;
        const response = localStorage.getItem(storageKey);
        if (response) {
          chapterEntries.push({
            question,
            response,
            chapterId: chapter.id,
            questionIndex: qIndex,
          });
        }
      });
      if (chapterEntries.length > 0) {
        allEntries.push({
          chapter,
          entries: chapterEntries,
        });
      }
    });
    setEntries(allEntries);
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
  };

  const deleteEntry = (chapterId, questionIndex) => {
    const storageKey = `journal-ch${chapterId}-q${questionIndex}`;
    localStorage.removeItem(storageKey);
    loadEntries();
    toast.success('Entry deleted');
  };

  const exportJournal = () => {
    let journalText = `${bookContent.title}\nMy Personal Journal\n${'='.repeat(50)}\n\n`;
    
    entries.forEach(({ chapter, entries: chapterEntries }) => {
      journalText += `\n${'─'.repeat(40)}\nChapter ${chapter.id}: ${chapter.title}\n${'─'.repeat(40)}\n\n`;
      chapterEntries.forEach(({ question, response }) => {
        journalText += `Q: ${question}\n\n${response}\n\n`;
      });
    });

    const blob = new Blob([journalText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'breakthrough-journal.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Journal exported successfully');
  };

  const totalQuestions = bookContent.chapters.reduce(
    (acc, ch) => acc + ch.journalQuestions.length, 0
  );
  const answeredQuestions = entries.reduce(
    (acc, { entries: e }) => acc + e.length, 0
  );

  return (
    <div data-testid="my-journal-page" className="min-h-screen bg-cream py-8 md:py-12">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/"
            data-testid="journal-home-btn"
            className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors"
          >
            <Home className="w-5 h-5" />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <Link 
            to="/contents"
            data-testid="journal-contents-btn"
            className="flex items-center gap-2 text-charcoal-light hover:text-sage transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            <span className="hidden sm:inline">Chapters</span>
          </Link>
        </div>

        {/* Title */}
        <div className="text-center mb-10 animate-fade-in">
          <h1 className="font-accent text-5xl text-sage mb-2">My Journal</h1>
          <p className="text-charcoal-light">
            Your personal reflections from the journey
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-10">
          <div className="bg-white rounded-xl p-5 text-center shadow-sm">
            <p className="text-3xl font-heading text-sage">{answeredQuestions}</p>
            <p className="text-sm text-charcoal-light">Reflections Written</p>
          </div>
          <div className="bg-white rounded-xl p-5 text-center shadow-sm">
            <p className="text-3xl font-heading text-gold">{totalQuestions - answeredQuestions}</p>
            <p className="text-sm text-charcoal-light">Questions Remaining</p>
          </div>
        </div>

        {/* Export & Cloud Sync Buttons */}
        <div className="mb-8 flex flex-wrap justify-center gap-4">
          <CloudSync />
          {entries.length > 0 && (
            <button
              onClick={exportJournal}
              data-testid="export-journal-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-sage border border-sage/20 rounded-full font-medium hover:bg-sage/5 transition-all"
            >
              <Download className="w-4 h-4" />
              Export as Text
            </button>
          )}
        </div>

        {/* Journal Entries */}
        {entries.length > 0 ? (
          <div className="space-y-6">
            {entries.map(({ chapter, entries: chapterEntries }) => (
              <div 
                key={chapter.id}
                data-testid={`journal-chapter-${chapter.id}`}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggleChapter(chapter.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <img 
                      src={chapter.image} 
                      alt={chapter.title}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="text-left">
                      <span className="text-xs font-medium text-gold uppercase tracking-wider">
                        Chapter {chapter.id}
                      </span>
                      <h3 className="font-heading text-lg text-charcoal">
                        {chapter.title}
                      </h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-charcoal-light">
                      {chapterEntries.length} {chapterEntries.length === 1 ? 'entry' : 'entries'}
                    </span>
                    {expandedChapters[chapter.id] ? (
                      <ChevronUp className="w-5 h-5 text-charcoal-light" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-charcoal-light" />
                    )}
                  </div>
                </button>

                {expandedChapters[chapter.id] && (
                  <div className="border-t border-stone-100 p-5 space-y-6">
                    {chapterEntries.map((entry, idx) => (
                      <div 
                        key={idx}
                        data-testid={`entry-${chapter.id}-${entry.questionIndex}`}
                        className="relative"
                      >
                        <p className="text-charcoal font-medium mb-3 pr-10">
                          {entry.question}
                        </p>
                        <div className="bg-cream-paper rounded-lg p-4">
                          <p className="text-charcoal/80 whitespace-pre-wrap">
                            {entry.response}
                          </p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              data-testid={`delete-entry-${chapter.id}-${entry.questionIndex}`}
                              className="absolute top-0 right-0 p-2 text-charcoal-light hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this reflection?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. Your reflection will be permanently deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteEntry(chapter.id, entry.questionIndex)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                    <Link
                      to={`/chapter/${chapter.id}`}
                      className="inline-block text-sage hover:text-sage-dark font-medium text-sm"
                    >
                      Return to Chapter →
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 animate-fade-in">
            <img 
              src="https://images.unsplash.com/photo-1564247589577-11141973c096?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Woman reading"
              className="w-48 h-48 object-cover rounded-full mx-auto mb-8 shadow-lg"
            />
            <h3 className="font-heading text-2xl text-charcoal mb-3">
              Your Journal Awaits
            </h3>
            <p className="text-charcoal-light mb-8 max-w-md mx-auto">
              As you journey through each chapter and reflect on the questions, 
              your responses will be gathered here.
            </p>
            <Link
              to="/chapter/1"
              data-testid="start-journey-from-journal"
              className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg"
            >
              Begin Chapter 1
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
